import React, { useState, useEffect, useRef, useContext } from 'react';
import {
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Box,
  Button,
  IconButton,
  Tabs,
  Tab,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Chip,
  OutlinedInput,
  Checkbox,
  ListItemText,
} from '@mui/material';
import { 
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as ChartTooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  LineChart,
  Line,
  CartesianGrid,
} from 'recharts';
import { 
  Refresh,
  ArrowBack
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import CurrencyFormatter from '../utils/CurrencyFormatter';
import formatDateToStandard from '../utils/DateFormatter';
import TanStackTable from '../components/TanStackTable';
import CustomDatePicker from '../components/CustomDatePicker';

// Import services
import {
  getUserOrganizations,
  getUniversalSalesReports,
  getUniversalPurchaseReports,
  getUniversalInventoryReports,
  getUniversalCustomerReports,
  getUniversalSupplierReports,
} from '../services/reportsService';

// Reuse the CustomTooltip component from Reports.js (themed)
const CustomTooltip = ({ active, payload, label, type }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
  <div className="chart-tooltip">
        {type === 'inventory' ? (
          <>
            <p><strong>Product:</strong> {data.name}</p>
            <p><strong>Quantity:</strong> {data.value}</p>
            <p><strong>Category:</strong> {data.category}</p>
          </>
        ) : type === 'inventory-levels' ? (
          <>
            <p><strong>{data.name}</strong></p>
            <p><strong>Count:</strong> {data.value}</p>
            <p><strong>Products:</strong></p>
    <ul>
              {data.products?.map((product, idx) => (
                <li key={idx}>{product.name} ({product.quantity})</li>
              )) || 'No products'}
            </ul>
          </>
        ) : type === 'orders' ? (
          <>
            <p><strong>Date:</strong> {formatDateToStandard(data.date)}</p>
            <p><strong>Orders:</strong> {data.count}</p>
            <p><strong>Total Amount:</strong> <CurrencyFormatter value={data.amount} /></p>
          </>
        ) : type === 'orders-timeline' ? (
          <>
            <p><strong>Date:</strong> {formatDateToStandard(label)}</p>
            <p><strong>Sales Orders:</strong> {payload[0].payload.salesCount} orders</p>
            <p><strong>Sales Amount:</strong> <CurrencyFormatter value={payload[0].payload.salesAmount} /></p>
            <p><strong>Purchase Orders:</strong> {payload[0].payload.purchaseCount} orders</p>
            <p><strong>Purchase Amount:</strong> <CurrencyFormatter value={payload[0].payload.purchaseAmount} /></p>
          </>
        ) : type === 'inventory-movements' ? (
          <>
            <p><strong>Date:</strong> {formatDateToStandard(label)}</p>
            {payload.map((entry, index) => (
              <p key={index} style={{ color: entry.color }}>
                <strong>{entry.name}:</strong> {entry.value} units
              </p>
            ))}
          </>
        ) : (
          <>
            <p><strong>{label}</strong></p>
            <p><strong>Value:</strong> {data.value}</p>
          </>
        )}
      </div>
    );
  }
  return null;
};

const UniversalReportsPage = () => {
  const { isAuthenticated } = useContext(AuthContext);
  const navigate = useNavigate();
  
  // Add handleBack function
  const handleBack = () => {
    navigate('/list-orgs');
  };

  const [loading, setLoading] = useState(false); // Changed to false initially
  const [tabValue, setTabValue] = useState(0);
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [timeRangeType, setTimeRangeType] = useState('today'); // Default: custom
  
  // State for organization selection
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrgIds, setSelectedOrgIds] = useState([]);
  
  // Individual loading states for each tab
  const [tabLoading, setTabLoading] = useState({
    0: false, // Sales
    1: false, // Purchases
    2: false, // Inventory
    3: false, // Customers
    4: false, // Suppliers
  });

  // Individual data loaded states
  const [tabDataLoaded, setTabDataLoaded] = useState({
    0: false, // Sales
    1: false, // Purchases
    2: false, // Inventory
    3: false, // Customers
    4: false, // Suppliers
  });
  
  // State for reports data
  const [salesData, setSalesData] = useState({
    summary: {},
    timeline: [],
    topCustomers: []
  });
  const [purchaseData, setPurchaseData] = useState({
    summary: {},
    timeline: [],
    topSuppliers: [],
    topProducts: [],
    purchasesByOrg: [],
    monthlyPurchaseTrend: [] // Use correct backend property
  });
  const [inventoryData, setInventoryData] = useState({
    summary: {},
    levels: [],
    products: [],
    byOrganization: [],
    lowStockProducts: []
  });
  const [customerData, setCustomerData] = useState({
    summary: {},
    customers: [],
    categoryBreakdown: [],
    monthlySales: [],
    byOrganization: [],
    topCustomers: []
  });
  const [supplierData, setSupplierData] = useState({
    summary: {},
    suppliers: [],
    categoryBreakdown: [],
    byOrganization: []
  });

  // Ref for reports container to enable copy functionality
  const reportsContainerRef = useRef(null);

  // Time range options (same as Reports.js)
  const timeRanges = [
    { value: 'custom', label: 'Custom Range' },
    { value: 'today', label: 'Today' },
    { value: 'yesterday', label: 'Yesterday' },
    { value: 'this_week', label: 'This Week' },
    { value: 'last_week', label: 'Last Week' },
    { value: 'this_month', label: 'This Month' },
    { value: 'last_month', label: 'Last Month' },
    { value: 'this_quarter', label: 'This Quarter' },
    { value: 'last_quarter', label: 'Last Quarter' },
    { value: 'this_year', label: 'This Year' },
    { value: 'last_year', label: 'Last Year' },
  ];

  // Check authentication and fetch organizations
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/auth', { replace: true });
      return;
    }

    const fetchOrganizations = async () => {
      try {
        const orgs = await getUserOrganizations();
        setOrganizations(orgs);
        
        // Default to selecting all organizations
        if (orgs.length > 0) {
          setSelectedOrgIds(orgs.map(org => org.org_id));
        }
      } catch (error) {
        console.error('Error fetching organizations:', error);
      }
    };

    fetchOrganizations();
  }, [isAuthenticated, navigate]);

  // Format date for API requests
  // Format date for API requests (YYYY-MM-DD format required by backend)
  const formatDateForAPI = (date) => {
    return date.toISOString().split('T')[0]; // YYYY-MM-DD format for API
  };

  // Safe date formatting for HTML date input display (YYYY-MM-DD format required by HTML)
  const formatDateForDisplay = (date) => {
    try {
      if (!date || isNaN(date.getTime())) {
        return '';
      }
      // Use local date components for HTML date input (YYYY-MM-DD format)
      const year = date.getFullYear();
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const day = date.getDate().toString().padStart(2, '0');
      return `${year}-${month}-${day}`;
    } catch (error) {
      console.warn('Invalid date provided to formatDateForDisplay:', date);
      return '';
    }
  };

  // Calculate date range based on the selected time range type
  const calculateDateRange = (rangeType) => {
    const now = new Date();
    // Create dates in local timezone - this respects the user's system timezone
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    let start = new Date(startDate);
    let end = new Date(endDate);

    // Get dates based on time range - similar logic to Reports.js
    switch (rangeType) {
      case 'today':
        // Use exact local date - no timezone conversion
        start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        end = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        break;
      
      case 'yesterday':
        // Get yesterday in local timezone
        const yesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
        start = yesterday;
        end = yesterday;
        break;
      
      case 'this_week':
        start = new Date(today);
        // Get Monday of current week (getDay() returns 0 for Sunday, 1 for Monday, etc.)
        const dayOfWeek = start.getDay();
        const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // If Sunday (0), go back 6 days to Monday
        start.setDate(start.getDate() - daysFromMonday);
        end = today;
        break;
      
      case 'last_week':
        start = new Date(today);
        // Get Monday of current week first
        const currentDayOfWeek = start.getDay();
        const daysFromCurrentMonday = currentDayOfWeek === 0 ? 6 : currentDayOfWeek - 1;
        start.setDate(start.getDate() - daysFromCurrentMonday);
        // Then go back one more week to get last Monday
        start.setDate(start.getDate() - 7);
        end = new Date(start);
        end.setDate(end.getDate() + 6); // Sunday of last week
        break;
      
      case 'this_month':
        start = new Date(today.getFullYear(), today.getMonth(), 1);
        end = today;
        break;
      
      case 'last_month':
        start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        end = new Date(today.getFullYear(), today.getMonth(), 0);
        break;
      
      case 'this_quarter':
        const currentQuarter = Math.floor(today.getMonth() / 3);
        start = new Date(today.getFullYear(), currentQuarter * 3, 1);
        end = today;
        break;
      
      case 'last_quarter':
        const lastQuarter = Math.floor(today.getMonth() / 3) - 1;
        const yearOfLastQuarter = lastQuarter < 0 ? today.getFullYear() - 1 : today.getFullYear();
        const actualLastQuarter = lastQuarter < 0 ? 3 : lastQuarter;
        start = new Date(yearOfLastQuarter, actualLastQuarter * 3, 1);
        end = new Date(yearOfLastQuarter, (actualLastQuarter + 1) * 3, 0);
        break;
      
      case 'this_year':
        start = new Date(today.getFullYear(), 0, 1);
        end = today;
        break;
      
      case 'last_year':
        start = new Date(today.getFullYear() - 1, 0, 1);
        end = new Date(today.getFullYear() - 1, 11, 31);
        break;
      
      default: // custom - keep current start and end dates
        break;
    }
    
    // Only add 1 day if it's NOT today or yesterday (keep their exact local dates)
    start.setDate(start.getDate());
    end.setDate(end.getDate());
    
    return { start, end };
  };

  // Fetch specific tab data
  const fetchTabData = async (tabIndex) => {
    if (selectedOrgIds.length === 0) return;
    if (tabDataLoaded[tabIndex]) return; // Already loaded

    setTabLoading(prev => ({ ...prev, [tabIndex]: true }));

    try {
      let formattedStartDate, formattedEndDate;
      
      // For inventory reports, use a fixed wide date range that covers all data
      if (tabIndex === 2) {
        formattedStartDate = '1960-01-01'; // Fixed start date for inventory
        formattedEndDate = formatDateForAPI(new Date()); // Current date for inventory
      } else {
        formattedStartDate = formatDateForAPI(startDate);
        formattedEndDate = formatDateForAPI(endDate);
      }
      
      switch (tabIndex) {
        case 0: // Sales
          const salesReports = await getUniversalSalesReports(selectedOrgIds, formattedStartDate, formattedEndDate);
          setSalesData(salesReports);
          break;
        
        case 1: // Purchases
          const purchaseReports = await getUniversalPurchaseReports(selectedOrgIds, formattedStartDate, formattedEndDate);
          setPurchaseData(purchaseReports);
          break;
        
        case 2: // Inventory
          const inventoryReports = await getUniversalInventoryReports(selectedOrgIds, formattedStartDate, formattedEndDate);
          setInventoryData(inventoryReports);
          break;
        
        case 3: // Customers
          const customerReports = await getUniversalCustomerReports(selectedOrgIds, formattedStartDate, formattedEndDate);
          setCustomerData(customerReports);
          break;
        
        case 4: // Suppliers
          const supplierReports = await getUniversalSupplierReports(selectedOrgIds, formattedStartDate, formattedEndDate);
          setSupplierData(supplierReports);
          break;
      }

      setTabDataLoaded(prev => ({ ...prev, [tabIndex]: true }));
    } catch (error) {
      console.error(`Error fetching tab ${tabIndex} data:`, error);
    } finally {
      setTabLoading(prev => ({ ...prev, [tabIndex]: false }));
    }
  };

  // Handle tab change and fetch data if needed
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    fetchTabData(newValue);
  };

  // Reset data when organizations or date range changes
  const resetAllData = () => {
    setTabDataLoaded({
      0: false,
      1: false,
      2: false,
      3: false,
      4: false,
    });
    
    // Clear existing data
    setSalesData({ summary: {}, timeline: [], topCustomers: [] });
    setPurchaseData({ summary: {}, timeline: [], topSuppliers: [], topProducts: [], purchasesByOrg: [], monthlyPurchaseTrend: [] });
    setInventoryData({ summary: {}, levels: [], products: [], byOrganization: [], lowStockProducts: [] });
    setCustomerData({ summary: {}, customers: [], categoryBreakdown: [], monthlySales: [], byOrganization: [], topCustomers: [] });
    setSupplierData({ summary: {}, suppliers: [], categoryBreakdown: [], byOrganization: [] });
  };

  // Handle organization selection change
  const handleOrgSelectionChange = (event) => {
    const { value } = event.target;
    setSelectedOrgIds(value);
    resetAllData();
  };

  // Enhanced handler for manual date changes
  const handleDateChange = (field, value) => {
    // When user manually changes a date, switch to custom range
    if (timeRangeType !== 'custom') {
      setTimeRangeType('custom');
    }
    
    // Update the corresponding date field
    if (field === 'start') {
      setStartDate(new Date(value));
    } else if (field === 'end') {
      setEndDate(new Date(value));
    }
    
    resetAllData();
  };

  // Handle time range selection
  const handleTimeRangeChange = (event) => {
    const newRangeType = event.target.value;
    setTimeRangeType(newRangeType);
    
    if (newRangeType !== 'custom') {
      const { start, end } = calculateDateRange(newRangeType);
      setStartDate(start);
      setEndDate(end);
    }
    
    resetAllData();
  };

  // Handle refresh button click
  const handleRefresh = () => {
    resetAllData();
    fetchTabData(tabValue);
  };

  // Initial data fetch for current tab when organizations are set
  useEffect(() => {
    if (selectedOrgIds.length > 0) {
      fetchTabData(tabValue);
    }
  }, [selectedOrgIds, tabValue]); // Added tabValue to dependencies

  // Compute byOrganization and topCustomers for universal customer reports
  useEffect(() => {
    if (!customerData.customers || customerData.customers.length === 0) return;

    // Group by email domain as a proxy for organization (fallback: All Organizations)
    const orgCustomerMap = {};
    customerData.customers.forEach(cust => {
      if (cust.orderCount > 0) {
        let orgName = 'All Organizations';
        if (cust.email && cust.email.includes('@')) {
          orgName = cust.email.split('@')[1].split('.')[0]; // e.g. 'miller' from 'jillrhodes@miller.com'
        }
        if (!orgCustomerMap[orgName]) orgCustomerMap[orgName] = 0;
        orgCustomerMap[orgName]++;
      }
    });
    const byOrganization = Object.entries(orgCustomerMap).map(([orgName, customerCount]) => ({
      orgName,
      customerCount,
    }));

    // Top customers (orderCount > 0), add orgName as above
    const topCustomers = customerData.customers
      .filter(cust => cust.orderCount > 0)
      .sort((a, b) => b.totalSpent - a.totalSpent)
      .slice(0, 10)
      .map(cust => {
        let orgName = 'All Organizations';
        if (cust.email && cust.email.includes('@')) {
          orgName = cust.email.split('@')[1].split('.')[0];
        }
        return { ...cust, orgName };
      });

    setCustomerData(prev => ({
      ...prev,
      byOrganization,
      topCustomers,
    }));
  }, [customerData.customers]);

  // Compute byOrganization for universal supplier reports
  useEffect(() => {
    if (!supplierData.suppliers || supplierData.suppliers.length === 0) return;

    // Try to match supplier to orgName using email domain and selected organizations
    const orgSupplierMap = {};
    supplierData.suppliers.forEach(supplier => {
      if (supplier.orderCount > 0) {
        let orgName = 'All Organizations';
        if (supplier.email && supplier.email.includes('@')) {
          // Try to match domain to org name (case-insensitive, partial match)
          const domain = supplier.email.split('@')[1].split('.')[0].toLowerCase();
          const matchedOrg = organizations.find(org =>
            org.org_name && org.org_name.toLowerCase().includes(domain)
          );
          if (matchedOrg) {
            orgName = matchedOrg.org_name;
          } else {
            orgName = domain.charAt(0).toUpperCase() + domain.slice(1);
          }
        }
        if (!orgSupplierMap[orgName]) orgSupplierMap[orgName] = 0;
        orgSupplierMap[orgName]++;
      }
    });
    const byOrganization = Object.entries(orgSupplierMap).map(([orgName, supplierCount]) => ({
      orgName,
      supplierCount,
    }));

    setSupplierData(prev => ({
      ...prev,
      byOrganization,
    }));
  }, [supplierData.suppliers, organizations]);

  // Common styles (same as Reports.js)
  const COLORS = ['#FF8042', '#00C49F', '#FFBB28', '#0088FE', '#8884d8'];
  const cardStyle = { backgroundColor: 'var(--surface-card)', backdropFilter: 'saturate(140%) blur(6px)', border: '1px solid var(--border-color)', height: '100%', minHeight: '200px' };
  const cardHeaderStyle = { 
    borderBottom: '1px solid var(--border-color)',
    padding: '16px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  };

  // MENU PROPS for multi-select
  const ITEM_HEIGHT = 48;
  const ITEM_PADDING_TOP = 8;
  const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
        width: 250,
      },
    },
  };

  const renderUnitsBreakdown = (unitsBreakdown) => {
    if (!unitsBreakdown || unitsBreakdown.length === 0) return 'No data';
    return (
      <Box
        sx={{
          maxHeight: '150px', // Set a maximum height for the container
          overflowY: 'scroll', // Enable vertical scrolling
          padding: '8px',
          backgroundColor: '#2e2e2e',
          borderRadius: '4px',
        }}
      >
        {unitsBreakdown.map((item, index) => (
          <Typography
            key={index}
            variant="body2"
            sx={{ color: '#ffffff', marginBottom: '4px' }}
          >
            {item.quantity} {item.unit}
          </Typography>
        ))}
      </Box>
    );
  };

  const salesDetailsColumns = [
    { header: 'Date', accessorKey: 'orderDate' },
    { header: 'Order ID', accessorKey: 'id' },
    { header: 'Customer', accessorKey: 'customerName' },
    { header: 'Products', accessorKey: 'productCount' },
    { header: 'Amount', accessorKey: 'amount', cell: ({ getValue }) => <CurrencyFormatter value={getValue()} /> },
    { header: 'Status', accessorKey: 'status' },
  ];

  const customerDetailsColumns = [
    { header: 'Customer', accessorKey: 'name' },
    { header: 'Email', accessorKey: 'email' },
    { header: 'Orders', accessorKey: 'orderCount' },
    { header: 'Total Spent', accessorKey: 'totalSpent', cell: ({ getValue }) => <CurrencyFormatter value={getValue()} /> },
    { header: 'Avg. Order', accessorKey: 'averageOrderValue', cell: ({ getValue }) => <CurrencyFormatter value={getValue()} /> },
    { header: 'Last Order', accessorKey: 'lastOrderDate', cell: ({ getValue }) => formatDateToStandard(getValue()) },
  ];

  const supplierDetailsColumns = [
    { header: 'Supplier', accessorKey: 'name' },
    { header: 'Email', accessorKey: 'email' },
    { header: 'Orders', accessorKey: 'orderCount' },
    { header: 'Total Purchases', accessorKey: 'totalPurchased', cell: ({ getValue }) => <CurrencyFormatter value={getValue()} /> },
    { header: 'Avg. Order', accessorKey: 'averageOrderValue', cell: ({ getValue }) => <CurrencyFormatter value={getValue()} /> },
    { header: 'Last Order', accessorKey: 'lastOrderDate', cell: ({ getValue }) => formatDateToStandard(getValue()) },
  ];

  const components = {
    CurrencyFormatter: CurrencyFormatter, // Register the CurrencyFormatter
  };

  return (
    <Paper className="surface-card" sx={{ p: 3, mb: 6 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton 
          onClick={handleBack} 
          sx={{ mr: 2, color: 'text.primary' }}
          aria-label="back to organizations"
        >
          <ArrowBack />
        </IconButton>
        <Typography variant="h4" component="h1">
          Universal Reports
        </Typography>
      </Box>

      {/* Organization Selection and Date Range Controls */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', md: 'row' }, 
        justifyContent: 'space-between',
        alignItems: { xs: 'flex-start', md: 'center' },
        mb: 3,
        gap: 2
      }}>
        <FormControl sx={{ minWidth: 300, maxWidth: 500 }}>
          <InputLabel id="multi-org-select-label">Organizations</InputLabel>
          <Select
            labelId="multi-org-select-label"
            id="multi-org-select"
            multiple
            value={selectedOrgIds}
            onChange={handleOrgSelectionChange}
            input={<OutlinedInput label="Organizations" />}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map((orgId) => {
                  const org = organizations.find(o => o.org_id === orgId);
                  return (
                    <Chip 
                      key={orgId} 
                      label={org ? org.org_name : orgId} 
                      sx={{ color: '#fff' }}
                    />
                  );
                })}
              </Box>
            )}
            MenuProps={MenuProps}
          >
            {organizations.map((org) => (
              <MenuItem key={org.org_id} value={org.org_id}>
                <Checkbox checked={selectedOrgIds.indexOf(org.org_id) > -1} />
                <ListItemText primary={org.org_name} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2,
          mt: { xs: 2, md: 0 },
          width: { xs: '100%', md: 'auto' }
        }}>
          {/* Date Range Options */}
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <FormControl sx={{ minWidth: 150 }}>
              <InputLabel id="time-range-label">Time Period</InputLabel>
              <Select
                labelId="time-range-label"
                value={timeRangeType}
                label="Time Period"
                onChange={handleTimeRangeChange}
                size="small"
              >
                {timeRanges.map((range) => (
                  <MenuItem key={range.value} value={range.value}>
                    {range.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Date inputs - replaced with CustomDatePicker */}
            <CustomDatePicker
              label="Start Date"
              value={startDate}
              onChange={setStartDate}
              sx={{ minWidth: 150 }}
            />
            
            <CustomDatePicker
              label="End Date"
              value={endDate}
              onChange={setEndDate}
              sx={{ minWidth: 150 }}
            />
          </Box>
          
          <Button 
            variant="contained" 
            color="primary" 
            startIcon={<Refresh />} 
            onClick={handleRefresh}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Current Date Range Display */}
      <Box sx={{ mb: 2 }}>
  <Typography variant="body2" color="text.secondary">
          Showing data for {selectedOrgIds.length} organization(s) from: <b>{formatDateToStandard(startDate)}</b> to <b>{formatDateToStandard(endDate)}</b>
        </Typography>
  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontSize: '0.7rem' }}>
          Note: All dates are in dd-MMM-yyyy format
        </Typography>
      </Box>

      {/* Report Controls - Tabs and Export Options */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 2,
        flexDirection: { xs: 'column', sm: 'row' },
        gap: 2
      }}>
    <Tabs 
          value={tabValue} 
          onChange={handleTabChange} 
          variant="scrollable"
          scrollButtons="auto"
          sx={{ 
      '& .MuiTabs-indicator': { backgroundColor: 'primary.main' },
      '& .Mui-selected': { color: 'text.primary !important' },
      '& .MuiTab-root': { color: 'text.secondary' },
          }}
        >
          <Tab label="Sales" />
          <Tab label="Purchases" />
          <Tab label="Inventory" />
          <Tab label="Customers" />
          <Tab label="Suppliers" />
        </Tabs>
      </Box>

      {/* Main report content area */}
      {selectedOrgIds.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <Typography variant="h6">Please select at least one organization to view reports</Typography>
        </Box>
      ) : (
        <Box ref={reportsContainerRef} className="print-container">
          {/* SALES REPORT */}
          {tabValue === 0 && (
            <>
              {tabLoading[0] ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                  <CircularProgress />
                  <Typography variant="h6" sx={{ ml: 2 }}>Loading Sales Data...</Typography>
                </Box>
              ) : (
                <Grid container spacing={3}>
                  {/* Summary Cards */}
                  <Grid item xs={12}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6} md={3}>
                        <Card sx={cardStyle}>
                          <CardContent>
                            <Typography variant="subtitle1" sx={{ fontSize: '1rem', fontWeight: 'bold', mb: 1 }}>
                              Total Sales
                            </Typography>
                            <Typography variant="h5" sx={{ fontSize: '1.8rem' }}>
                              <CurrencyFormatter value={salesData.summary.totalAmount || 0} />
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                      
                      <Grid item xs={12} sm={6} md={3}>
                        <Card sx={cardStyle}>
                          <CardContent>
                            <Typography variant="subtitle1" sx={{ fontSize: '1rem', fontWeight: 'bold', mb: 1 }}>
                              Orders Count
                            </Typography>
                            <Typography variant="h5" sx={{ fontSize: '1.8rem' }}>
                              {salesData.summary.totalOrders || 0}
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                      
                      <Grid item xs={12} sm={6} md={3}>
                        <Card sx={cardStyle}>
                          <CardContent>
                            <Typography variant="subtitle1" sx={{ fontSize: '1rem', fontWeight: 'bold', mb: 1 }}>
                              Average Order Value
                            </Typography>
                            <Typography variant="h5" sx={{ fontSize: '1.8rem' }}>
                              <CurrencyFormatter value={salesData.summary.averageOrderValue || 0} />
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                      
                      <Grid item xs={12} sm={6} md={3}>
                        <Card sx={cardStyle}>
                          <CardContent>
                            <Typography variant="subtitle1" sx={{ fontSize: '1rem', fontWeight: 'bold', mb: 1 }}>
                              Products Sold
                            </Typography>
                            {renderUnitsBreakdown(salesData?.unitsSoldBreakdown)}
                          </CardContent>
                        </Card>
                      </Grid>
                    </Grid>
                  </Grid>

                  {/* Sales Timeline Chart */}
                  <Grid item xs={12}>
                    <Card sx={cardStyle}>
                      <Box sx={cardHeaderStyle}>
                        <Typography variant="h6">Sales Timeline</Typography>
                      </Box>
                      <CardContent sx={{ height: 350 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart
                            data={salesData.timeline}
                            margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                          >
                            <XAxis 
                              dataKey="date" 
                              tickFormatter={(date) => formatDateToStandard(date)} 
                            />
                            <YAxis yAxisId="left" orientation="left" stroke="#00C49F" />
                            <YAxis yAxisId="right" orientation="right" stroke="#FF8042" />
                            <ChartTooltip content={({ active, payload, label }) => {
                              if (active && payload && payload.length) {
                                const org = organizations.find(o => o.org_id === payload[0].payload.orgId);
                                const orgName = org ? org.org_name : `Organization ${payload[0].payload.orgId}`;
                                
                                return (
                                  <div style={{
                                    backgroundColor: '#333',
                                    padding: '10px',
                                    borderRadius: '5px',
                                    color: '#fff',
                                  }}>
                                    <p><strong>Date:</strong> {formatDateToStandard(label)}</p>
                                    <p><strong>Organization:</strong> {orgName}</p>
                                    <p><strong>Sales Count:</strong> {payload[0].payload.salesCount} orders</p>
                                    <p><strong>Sales Amount:</strong> <CurrencyFormatter value={payload[0].payload.salesAmount} /></p>
                                  </div>
                                );
                              }
                              return null;
                            }} />
                            <Legend />
                            <Line 
                              yAxisId="left"
                              type="monotone" 
                              dataKey="salesAmount" 
                              name="Sales Amount" 
                              stroke="#00C49F" 
                              activeDot={{ r: 8 }} 
                            />
                            <Line 
                              yAxisId="right"
                              type="monotone" 
                              dataKey="salesCount" 
                              name="Order Count" 
                              stroke="#FF8042" 
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Monthly Sales Trend - Modified to show by organization */}
                  <Grid item xs={12} md={6}>
                    <Card sx={cardStyle}>
                      <Box sx={cardHeaderStyle}>
                        <Typography variant="h6">Monthly Sales Trend by Organization</Typography>
                      </Box>
                      <CardContent sx={{ height: 350 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          {salesData.monthlySalesTrend && salesData.monthlySalesTrend.length > 0 ? (
                            <BarChart
                              data={(() => {
                                // Restructure data for monthly trend by organization
                                const monthlyOrgData = [];
                                const monthlyData = new Map();
                                
                                // Group timeline data by month and organization
                                salesData.timeline.forEach(entry => {
                                  const date = new Date(entry.date);
                                  const month = `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}`;
                                  const key = `${month}-${entry.orgId}`;
                                  
                                  if (!monthlyData.has(key)) {
                                    monthlyData.set(key, {
                                      month,
                                      orgId: entry.orgId,
                                      salesAmount: 0,
                                      salesCount: 0
                                    });
                                  }
                                  
                                  const record = monthlyData.get(key);
                                  record.salesAmount += entry.salesAmount;
                                  record.salesCount += entry.salesCount;
                                });
                                
                                // Convert to array and add organization names
                                Array.from(monthlyData.values()).forEach(item => {
                                  const org = organizations.find(o => o.org_id === item.orgId);
                                  monthlyOrgData.push({
                                    ...item,
                                    orgName: org ? org.org_name : `Org ${item.orgId}`
                                  });
                                });
                                
                                return monthlyOrgData.sort((a, b) => a.month.localeCompare(b.month));
                              })()}
                              margin={{ top: 20, right: 30, left: 20, bottom: 30 }}
                            >
                              <CartesianGrid strokeDasharray="3 3" opacity={0.15} />
                              <XAxis 
                                dataKey="month" 
                                angle={-45}
                                textAnchor="end"
                                height={70}
                              />
                              <YAxis />
                              <ChartTooltip 
                                formatter={(value, name) => {
                                  if (name === 'salesAmount') return <CurrencyFormatter value={value} />;
                                  return value;
                                }}
                                labelFormatter={(value) => `Month: ${value}`}
                              />
                              <Legend />
                              <Bar 
                                dataKey="salesAmount" 
                                name="Sales Amount" 
                                fill="#00C49F"
                                barSize={30}
                              >
                                {(() => {
                                  const bars = [];
                                  const orgs = [...new Set((salesData.timeline || []).map(item => item.orgId))];
                                  
                                  return orgs.map((orgId, index) => (
                                    <Cell 
                                      key={`cell-${orgId}`} 
                                      fill={COLORS[index % COLORS.length]}
                                      // This filter ensures only cells for this organization get this color
                                      fillOpacity={d => d.orgId === orgId ? 1 : 0}
                                    />
                                  ));
                                })()}
                              </Bar>
                            </BarChart>
                          ) : (
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                              <Typography variant="body1">No monthly data available</Typography>
                            </Box>
                          )}
                        </ResponsiveContainer>
                      </CardContent>
                    </Card>
                  </Grid>
                  
                  {/* Monthly Sales Trend - Stacked Area Chart Alternative */}
                  <Grid item xs={12}>
                    <Card sx={cardStyle}>
                      <Box sx={cardHeaderStyle}>
                        <Typography variant="h6">Monthly Sales Trend Comparison</Typography>
                      </Box>
                      <CardContent sx={{ height: 350 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          {salesData.stackedMonthlyData && salesData.stackedMonthlyData.length > 0 ? (
                            <BarChart
                              data={salesData.stackedMonthlyData}
                              margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                            >
                              <CartesianGrid strokeDasharray="3 3" opacity={0.15} />
                              <XAxis dataKey="month" />
                              <YAxis />
                              <ChartTooltip 
                                formatter={(value, name, props) => {
                                  // Display organization name instead of the data key
                                  if (name.startsWith('org') && name.endsWith('Amount')) {
                                    const orgId = name.replace('org', '').replace('Amount', '');
                                    const org = organizations.find(o => o.org_id === orgId);
                                    const orgName = org ? org.org_name : `Org ${orgId}`;
                                    return [<CurrencyFormatter value={value} />, orgName];
                                  }
                                  return <CurrencyFormatter value={value} />;
                                }}
                              />
                              <Legend 
                                formatter={(value, entry) => {
                                  // Convert 'org1Amount' to actual organization name
                                  if (value.startsWith('org') && value.endsWith('Amount')) {
                                    const orgId = value.replace('org', '').replace('Amount', '');
                                    const org = organizations.find(o => o.org_id === orgId);
                                    return org ? org.org_name : `Org ${orgId}`;
                                  }
                                  return value;
                                }}
                              />
                              {salesData.salesByOrg && salesData.salesByOrg.map((org, index) => {
                                return (
                                  <Bar 
                                    key={org.orgId}
                                    dataKey={`org${org.orgId}Amount`} 
                                    name={`org${org.orgId}Amount`} 
                                    fill={COLORS[index % COLORS.length]}
                                    stackId="a"
                                  />
                                );
                              })}
                            </BarChart>
                          ) : (
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                              <Typography variant="body1">No monthly data available</Typography>
                            </Box>
                          )}
                        </ResponsiveContainer>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Top Customers */}
                  <Grid item xs={12}>
                    <Card sx={cardStyle}>
                      <Box sx={cardHeaderStyle}>
                        <Typography variant="h6">Top Customers</Typography>
                      </Box>
                      <CardContent>
                        <TanStackTable
                          data={salesData.topCustomers || []}
                          columns={[
                            { header: 'Customer', accessorKey: 'name' },
                            { header: 'Organization', accessorKey: 'orgName' },
                            { header: 'Orders', accessorKey: 'orderCount' },
                            { header: 'Total Amount', accessorKey: 'totalAmount', cell: ({ getValue }) => <CurrencyFormatter value={getValue()} /> },
                            { header: 'Average Order Value', accessorKey: 'averageOrderValue', cell: ({ getValue }) => <CurrencyFormatter value={getValue()} /> },
                            { header: 'Last Order Date', accessorKey: 'lastOrderDate', cell: ({ getValue }) => formatDateToStandard(getValue()) },
                          ]}
                        />
                      </CardContent>
                    </Card>
                  </Grid>

                </Grid>
              )}
            </>
          )}

          {/* PURCHASE REPORT */}
          {tabValue === 1 && (
            <>
              {tabLoading[1] ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                  <CircularProgress />
                  <Typography variant="h6" sx={{ ml: 2 }}>Loading Purchase Data...</Typography>
                </Box>
              ) : (
                <Grid container spacing={3}>
                  {/* Purchase summary cards */}
                  <Grid item xs={12}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6} md={3}>
                        <Card sx={cardStyle}>
                          <CardContent>
                            <Typography variant="subtitle1" sx={{ fontSize: '1rem', fontWeight: 'bold', mb: 1 }}>
                              Total Purchases
                            </Typography>
                            <Typography variant="h5" sx={{ fontSize: '1.8rem' }}>
                              <CurrencyFormatter value={purchaseData.summary?.totalAmount || 0} />
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                      
                      <Grid item xs={12} sm={6} md={3}>
                        <Card sx={cardStyle}>
                          <CardContent>
                            <Typography variant="subtitle1" sx={{ fontSize: '1rem', fontWeight: 'bold', mb: 1 }}>
                              Orders Count
                            </Typography>
                            <Typography variant="h5" sx={{ fontSize: '1.8rem' }}>
                              {purchaseData.summary?.totalOrders || 0}
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                      
                      <Grid item xs={12} sm={6} md={3}>
                        <Card sx={cardStyle}>
                          <CardContent>
                            <Typography variant="subtitle1" sx={{ fontSize: '1rem', fontWeight: 'bold', mb: 1 }}>
                              Average Order Cost
                            </Typography>
                            <Typography variant="h5" sx={{ fontSize: '1.8rem' }}>
                              <CurrencyFormatter value={purchaseData.summary?.averageOrderValue || 0} />
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                      
                      <Grid item xs={12} sm={6} md={3}>
                        <Card sx={cardStyle}>
                          <CardContent>
                            <Typography variant="subtitle1" sx={{ fontSize: '1rem', fontWeight: 'bold', mb: 1 }}>
                              Units Purchased
                            </Typography>
                            {renderUnitsBreakdown(purchaseData?.unitsPurchasedBreakdown)}
                          </CardContent>
                        </Card>
                      </Grid>
                    </Grid>
                  </Grid>

                  {/* Purchase Timeline Chart */}
                  <Grid item xs={12}>
                    <Card sx={cardStyle}>
                      <Box sx={cardHeaderStyle}>
                        <Typography variant="h6">Purchase Timeline</Typography>
                      </Box>
                      <CardContent sx={{ height: 350 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          {purchaseData.timeline && purchaseData.timeline.length > 0 ? (
                            <LineChart
                              data={purchaseData.timeline}
                              margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                            >
                              <XAxis 
                                dataKey="date" 
                                tickFormatter={(date) => formatDateToStandard(date)} 
                              />
                              <YAxis yAxisId="left" orientation="left" stroke="#00C49F" />
                              <YAxis yAxisId="right" orientation="right" stroke="#FF8042" />
                              <ChartTooltip content={({ active, payload, label }) => {
                                if (active && payload && payload.length) {
                                  const org = organizations.find(o => o.org_id === payload[0].payload.orgId);
                                  const orgName = org ? org.org_name : `Organization ${payload[0].payload.orgId}`;
                                  
                                  return (
                                    <div style={{
                                      backgroundColor: '#333',
                                      padding: '10px',
                                      borderRadius: '5px',
                                      color: '#fff',
                                    }}>
                                      <p><strong>Date:</strong> {formatDateToStandard(label)}</p>
                                      <p><strong>Organization:</strong> {orgName}</p>
                                      <p><strong>Purchase Count:</strong> {payload[0].payload.purchaseCount} orders</p>
                                      <p><strong>Purchase Amount:</strong> <CurrencyFormatter value={payload[0].payload.purchaseAmount} /></p>
                                    </div>
                                  );
                                }
                                return null;
                              }} />
                              <Legend />
                              <Line 
                                yAxisId="left"
                                type="monotone" 
                                dataKey="purchaseAmount" 
                                name="Purchase Amount" 
                                stroke="#00C49F" 
                                activeDot={{ r: 8 }} 
                              />
                              <Line 
                                yAxisId="right"
                                type="monotone" 
                                dataKey="purchaseCount" 
                                name="Order Count" 
                                stroke="#FF8042" 
                              />
                            </LineChart>
                          ) : (
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                              <Typography variant="body1">No purchase timeline data available</Typography>
                            </Box>
                          )}
                        </ResponsiveContainer>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Top Suppliers */}
                  <Grid item xs={12}>
                    <Card sx={cardStyle}>
                      <Box sx={cardHeaderStyle}>
                        <Typography variant="h6">Top Suppliers</Typography>
                      </Box>
                      <CardContent>
                        {purchaseData.topSuppliers && purchaseData.topSuppliers.length > 0 ? (
                          <TanStackTable
                            data={purchaseData.topSuppliers}
                            columns={[
                              { header: 'Supplier', accessorKey: 'name' },
                              { header: 'Organization', accessorKey: 'orgName' },
                              { header: 'Orders', accessorKey: 'orderCount' },
                              { header: 'Total Amount', accessorKey: 'totalAmount', cell: ({ getValue }) => <CurrencyFormatter value={getValue()} /> },
                              { header: 'Average Order Value', accessorKey: 'averageOrderValue', cell: ({ getValue }) => <CurrencyFormatter value={getValue()} /> },
                              { header: 'Last Order Date', accessorKey: 'lastOrderDate', cell: ({ getValue }) => getValue ? formatDateToStandard(getValue()) : 'N/A' },
                            ]}
                          />
                        ) : (
                          <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                            <Typography variant="body1">No supplier data available</Typography>
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Purchases by Organization */}
                  <Grid item xs={12} md={6} mb={4}>
                    <Card sx={cardStyle}>
                      <Box sx={cardHeaderStyle}>
                        <Typography variant="h6">Purchases by Organization</Typography>
                      </Box>
                      <CardContent sx={{ height: 350 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          {purchaseData.purchasesByOrg && purchaseData.purchasesByOrg.length > 0 ? (
                            <BarChart
                              data={purchaseData.purchasesByOrg}
                              margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                            >
                              <XAxis dataKey="orgName" />
                              <YAxis />
                              <ChartTooltip formatter={(value) => <CurrencyFormatter value={value} />} />
                              <Legend />
                              <Bar dataKey="totalAmount" name="Purchase Amount" fill="#FF8042">
                                {purchaseData.purchasesByOrg.map((entry, index) => (
                                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                ))}
                              </Bar>
                            </BarChart>
                          ) : (
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                              <Typography variant="body1">No purchase data by organization available</Typography>
                            </Box>
                          )}
                        </ResponsiveContainer>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Monthly Purchase Trend */}
                  <Grid item xs={12} md={6} mb = {4}>
                    <Card sx={cardStyle}>
                      <Box sx={cardHeaderStyle}>
                        <Typography variant="h6">Monthly Purchase Trend</Typography>
                      </Box>
                      <CardContent sx={{ height: 350 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          {purchaseData.monthlyPurchaseTrend && purchaseData.monthlyPurchaseTrend.length > 0 ? (
                            <LineChart
                              data={purchaseData.monthlyPurchaseTrend}
                              margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                            >
                              <XAxis dataKey="month" />
                              <YAxis yAxisId="left" orientation="left" stroke="#00C49F" />
                              <YAxis yAxisId="right" orientation="right" stroke="#FF8042" />
                              <ChartTooltip formatter={(value, name) => {
                                // Show currency for purchaseAmount, plain for purchaseCount
                                if (name === 'purchaseAmount') return <CurrencyFormatter value={value} />;
                                return value;
                              }} />
                              <Legend />
                              <Line 
                                yAxisId="left"
                                type="monotone" 
                                dataKey="purchaseAmount" 
                                name="Purchase Amount" 
                                stroke="#00C49F" 
                                activeDot={{ r: 8 }} 
                              />
                              <Line 
                                yAxisId="right"
                                type="monotone" 
                                dataKey="purchaseCount" 
                                name="Order Count" 
                                stroke="#FF8042" 
                              />
                            </LineChart>
                          ) : (
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                              <Typography variant="body1">No monthly trend data available</Typography>
                            </Box>
                          )}
                        </ResponsiveContainer>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              )}
            </>
          )}

          {/* INVENTORY REPORT */}
          {tabValue === 2 && (
            <>
              {tabLoading[2] ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                  <CircularProgress />
                  <Typography variant="h6" sx={{ ml: 2 }}>Loading Inventory Data...</Typography>
                </Box>
              ) : (
                <Grid container spacing={3}>
                  {/* Inventory summary cards */}
                  <Grid item xs={12}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6} md={3}>
                        <Card sx={cardStyle}>
                          <CardContent>
                            <Typography variant="subtitle1" sx={{ fontSize: '1rem', fontWeight: 'bold', mb: 1 }}>
                              Total Products
                            </Typography>
                            <Typography variant="h5" sx={{ fontSize: '1.8rem' }}>
                              {inventoryData.summary?.totalProducts || 0}
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                      
                      <Grid item xs={12} sm={6} md={3}>
                        <Card sx={cardStyle}>
                          <CardContent>
                            <Typography variant="subtitle1" sx={{ fontSize: '1rem', fontWeight: 'bold', mb: 1 }}>
                              Total Units
                            </Typography>
                            <Typography variant="h5" sx={{ fontSize: '1.8rem' }}>
                              {inventoryData.summary?.totalUnits || 0}
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                      
                      <Grid item xs={12} sm={6} md={3}>
                        <Card sx={cardStyle}>
                          <CardContent>
                            <Typography variant="subtitle1" sx={{ fontSize: '1rem', fontWeight: 'bold', mb: 1 }}>
                              Low Stock Items
                            </Typography>
                            <Typography variant="h5" sx={{ fontSize: '1.8rem', color: '#ff6b6b' }}>
                              {inventoryData.summary?.lowStockCount || 0}
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                      
                      <Grid item xs={12} sm={6} md={3}>
                        <Card sx={cardStyle}>
                          <CardContent>
                            <Typography variant="subtitle1" sx={{ fontSize: '1rem', fontWeight: 'bold', mb: 1 }}>
                              Stock Value
                            </Typography>
                            <Typography variant="h5" sx={{ fontSize: '1.8rem' }}>
                              <CurrencyFormatter value={inventoryData.summary?.totalValue || 0} />
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    </Grid>
                  </Grid>

                  {/* Inventory levels */}
                  <Grid item xs={12} md={6}>
                    <Card sx={cardStyle}>
                      <Box sx={cardHeaderStyle}>
                        <Typography variant="h6">Inventory Levels</Typography>
                      </Box>
                      <CardContent sx={{ height: 350 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          {inventoryData.levels && inventoryData.levels.length > 0 ? (
                            <BarChart
                              data={inventoryData.levels}
                              margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                            >
                              <XAxis dataKey="name" />
                              <YAxis />
                              <ChartTooltip content={<CustomTooltip type="inventory-levels" />} />
                              <Bar dataKey="value">
                                {inventoryData.levels.map((entry, index) => (
                                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                ))}
                              </Bar>
                            </BarChart>
                          ) : (
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                              <Typography variant="body1">No inventory levels data available</Typography>
                            </Box>
                          )}
                        </ResponsiveContainer>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Inventory by Organization */}
                  <Grid item xs={12} md={6}>
                    <Card sx={cardStyle}>
                      <Box sx={cardHeaderStyle}>
                        <Typography variant="h6">Inventory by Organization</Typography>
                      </Box>
                      <CardContent sx={{ height: 350 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          {inventoryData.byOrganization && inventoryData.byOrganization.length > 0 ? (
                            <PieChart>
                              <Pie
                                data={inventoryData.byOrganization}
                                dataKey="value"
                                nameKey="orgName"
                                cx="50%"
                                cy="50%"
                                outerRadius={80}
                                fill="#8884d8"
                                label={({ name, value, percent }) => `${name}: ${value} (${(percent * 100).toFixed(0)}%)`}
                              >
                                {inventoryData.byOrganization.map((entry, index) => (
                                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                ))}
                              </Pie>
                              <ChartTooltip formatter={(value, name) => [`${value} units`, name]} />
                              <Legend />
                            </PieChart>
                          ) : (
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                              <Typography variant="body1">No organization data available</Typography>
                            </Box>
                          )}
                        </ResponsiveContainer>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Low Stock Products Table */}
                  <Grid item xs={12}>
                    <Card sx={cardStyle}>
                      <Box sx={cardHeaderStyle}>
                        <Typography variant="h6">Low Stock Products</Typography>
                        <Typography variant="body2" color="textSecondary">
                          {inventoryData.lowStockProducts?.length || 0} items need attention
                        </Typography>
                      </Box>
                      <CardContent>
                        {inventoryData.lowStockProducts && inventoryData.lowStockProducts.length > 0 ? (
                          <TanStackTable
                            data={inventoryData.lowStockProducts}
                            columns={[
                              { header: 'Product', accessorKey: 'name' },
                              { header: 'Organization', accessorKey: 'orgName' },
                              { header: 'SKU', accessorKey: 'sku' },
                              { header: 'Category', accessorKey: 'category' },
                              { header: 'Quantity', accessorKey: 'quantity' },
                              { header: 'Value', accessorKey: 'value', cell: ({ getValue }) => <CurrencyFormatter value={getValue()} /> },
                              { header: 'Status', accessorKey: 'status', cell: () => (
                                <Box 
                                  sx={{ 
                                    bgcolor: 'error.main',
                                    px: 1,
                                    py: 0.5,
                                    borderRadius: 1,
                                    display: 'inline-block',
                                    fontSize: '0.8rem',
                                    color: 'common.white'
                                  }}
                                >
                                  Low Stock
                                </Box>
                              ) },
                            ]}
                          />
                        ) : (
                          <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                            <Typography variant="body1" color="success.main">
                              🎉 No low stock products found across all organizations!
                            </Typography>
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>

                </Grid>
              )}
            </>
          )}

          {/* CUSTOMERS REPORT */}
          {tabValue === 3 && (
            <>
              {tabLoading[3] ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                  <CircularProgress />
                  <Typography variant="h6" sx={{ ml: 2 }}>Loading Customer Data...</Typography>
                </Box>
              ) : (
                <Grid container spacing={3}>
                  {/* Customer Summary Cards */}
                  <Grid item xs={12}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6} md={3}>
                        <Card sx={cardStyle}>
                          <CardContent>
                            <Typography variant="subtitle1" sx={{ fontSize: '1rem', fontWeight: 'bold', mb: 1 }}>
                              Total Customers
                            </Typography>
                            <Typography variant="h5" sx={{ fontSize: '1.8rem' }}>
                              {customerData.summary?.totalCustomers || 0}
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                      
                      <Grid item xs={12} sm={6} md={3}>
                        <Card sx={cardStyle}>
                          <CardContent>
                            <Typography variant="subtitle1" sx={{ fontSize: '1rem', fontWeight: 'bold', mb: 1 }}>
                              Active Customers
                            </Typography>
                            <Typography variant="h5" sx={{ fontSize: '1.8rem' }}>
                              {customerData.summary?.activeCustomers || 0}
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                      
                      <Grid item xs={12} sm={6} md={3}>
                        <Card sx={cardStyle}>
                          <CardContent>
                            <Typography variant="subtitle1" sx={{ fontSize: '1rem', fontWeight: 'bold', mb: 1 }}>
                              Total Sales
                            </Typography>
                            <Typography variant="h5" sx={{ fontSize: '1.8rem' }}>
                              <CurrencyFormatter value={customerData.summary?.totalSales || 0} />
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                      
                      <Grid item xs={12} sm={6} md={3}>
                        <Card sx={cardStyle}>
                          <CardContent>
                            <Typography variant="subtitle1" sx={{ fontSize: '1rem', fontWeight: 'bold', mb: 1 }}>
                              Average Order Value
                            </Typography>
                            <Typography variant="h5" sx={{ fontSize: '1.8rem' }}>
                              <CurrencyFormatter value={customerData.summary?.averageOrderValue || 0} />
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    </Grid>
                  </Grid>

                  {/* Customer Purchases by Category */}
                  <Grid item xs={12} md={6}>
                    <Card sx={cardStyle}>
                      <Box sx={cardHeaderStyle}>
                        <Typography variant="h6">Purchases by Category</Typography>
                      </Box>
                      <CardContent sx={{ height: 350 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={customerData.categoryBreakdown || []}
                              dataKey="totalAmount"
                              nameKey="category"
                              cx="50%"
                              cy="50%"
                              outerRadius={80}
                              fill="#8884d8"
                              label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                            >
                              {(customerData.categoryBreakdown || []).map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                              ))}
                            </Pie>
                            <ChartTooltip formatter={(value) => <CurrencyFormatter value={value} />} />
                            <Legend />
                          </PieChart>
                        </ResponsiveContainer>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Monthly Sales Chart */}
                  <Grid item xs={12} md={6}>
                    <Card sx={cardStyle}>
                      <Box sx={cardHeaderStyle}>
                        <Typography variant="h6">Monthly Sales</Typography>
                      </Box>
                      <CardContent sx={{ height: 350 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart
                            data={customerData.monthlySales || []}
                            margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                          >
                            <XAxis dataKey="month" />
                            <YAxis yAxisId="left" orientation="left" stroke="#00C49F" />
                            <YAxis yAxisId="right" orientation="right" stroke="#FF8042" />
                            <ChartTooltip formatter={(value, name) => {
                              return name === 'amount' 
                                ? <CurrencyFormatter value={value} />
                                : value;
                            }} />
                            <Legend />
                            <Bar yAxisId="left" dataKey="amount" name="Sales Amount" fill="#00C49F" />
                            <Bar yAxisId="right" dataKey="count" name="Order Count" fill="#FF8042" />
                          </BarChart>
                        </ResponsiveContainer>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Customer Count by Organization */}
                  <Grid item xs={12} md={6}>
                    <Card sx={cardStyle}>
                      <Box sx={cardHeaderStyle}>
                        <Typography variant="h6">Customers by Organization</Typography>
                      </Box>
                      <CardContent sx={{ height: 350 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart
                            data={customerData.byOrganization || []}
                            margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                          >
                            <XAxis dataKey="orgName" />
                            <YAxis />
                            <ChartTooltip />
                            <Legend />
                            <Bar dataKey="customerCount" name="Customer Count" fill="#00C49F">
                              {(customerData.byOrganization || []).map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                              ))}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Top Customers Across Organizations */}
                  <Grid item xs={12} mb={4}>
                    <Card sx={cardStyle}>
                      <Box sx={cardHeaderStyle}>
                        <Typography variant="h6">Top Customers Across Organizations</Typography>
                      </Box>
                      <CardContent>
                        <TanStackTable
                          data={customerData.topCustomers || []}
                          columns={[
                            { header: 'Customer', accessorKey: 'name' },
                            { header: 'Organization', accessorKey: 'orgName' },
                            { header: 'Orders', accessorKey: 'orderCount' },
                            { header: 'Total Spent', accessorKey: 'totalSpent', cell: ({ getValue }) => <CurrencyFormatter value={getValue()} /> },
                            { header: 'Avg. Order', accessorKey: 'averageOrderValue', cell: ({ getValue }) => <CurrencyFormatter value={getValue()} /> },
                            { header: 'Last Order', accessorKey: 'lastOrderDate', cell: ({ getValue }) => getValue ? formatDateToStandard(getValue()) : 'N/A' },
                          ]}
                        />
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              )}
            </>
          )}

          {/* SUPPLIERS REPORT */}
          {tabValue === 4 && (
            <>
              {tabLoading[4] ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                  <CircularProgress />
                  <Typography variant="h6" sx={{ ml: 2 }}>Loading Supplier Data...</Typography>
                </Box>
              ) : (
                <Grid container spacing={3}>
                  {/* Supplier Summary Cards */}
                  <Grid item xs={12}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6} md={3}>
                        <Card sx={cardStyle}>
                          <CardContent>
                            <Typography variant="subtitle1" sx={{ fontSize: '1rem', fontWeight: 'bold', mb: 1 }}>
                              Total Suppliers
                            </Typography>
                            <Typography variant="h5" sx={{ fontSize: '1.8rem' }}>
                              {supplierData.summary?.totalSuppliers || 0}
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                      
                      <Grid item xs={12} sm={6} md={3}>
                        <Card sx={cardStyle}>
                          <CardContent>
                            <Typography variant="subtitle1" sx={{ fontSize: '1rem', fontWeight: 'bold', mb: 1 }}>
                              Active Suppliers
                            </Typography>
                            <Typography variant="h5" sx={{ fontSize: '1.8rem' }}>
                              {supplierData.summary?.activeSuppliers || 0}
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                      
                      <Grid item xs={12} sm={6} md={3}>
                        <Card sx={cardStyle}>
                          <CardContent>
                            <Typography variant="subtitle1" sx={{ fontSize: '1rem', fontWeight: 'bold', mb: 1 }}>
                              Total Purchases
                            </Typography>
                            <Typography variant="h5" sx={{ fontSize: '1.8rem' }}>
                              <CurrencyFormatter value={supplierData.summary?.totalPurchased || 0} />
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                      
                      <Grid item xs={12} sm={6} md={3}>
                        <Card sx={cardStyle}>
                          <CardContent>
                            <Typography variant="subtitle1" sx={{ fontSize: '1rem', fontWeight: 'bold', mb: 1 }}>
                              Average Order Value
                            </Typography>
                            <Typography variant="h5" sx={{ fontSize: '1.8rem' }}>
                              <CurrencyFormatter value={supplierData.summary?.averageOrderValue || 0} />
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    </Grid>
                  </Grid>

                  {/* Category Distribution */}
                  <Grid item xs={12} md={6}>
                    <Card sx={cardStyle}>
                      <Box sx={cardHeaderStyle}>
                        <Typography variant="h6">Product Categories</Typography>
                      </Box>
                      <CardContent sx={{ height: 350 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={supplierData.categoryBreakdown || []}
                              dataKey="totalAmount"
                              nameKey="category"
                              cx="50%"
                              cy="50%"
                              outerRadius={80}
                              fill="#8884d8"
                              label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                            >
                              {(supplierData.categoryBreakdown || []).map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                              ))}
                            </Pie>
                            <ChartTooltip formatter={(value) => <CurrencyFormatter value={value} />} />
                            <Legend />
                          </PieChart>
                        </ResponsiveContainer>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Supplier Distribution By Organization */}
                  <Grid item xs={12} md={6}>
                    <Card sx={cardStyle}>
                      <Box sx={cardHeaderStyle}>
                        <Typography variant="h6">Suppliers by Organization</Typography>
                      </Box>
                      <CardContent sx={{ height: 350 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart
                            data={supplierData.byOrganization || []}
                            margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                          >
                            <XAxis dataKey="orgName" />
                            <YAxis />
                            <ChartTooltip />
                            <Legend />
                            <Bar dataKey="supplierCount" name="Supplier Count" fill="#FF8042">
                              {(supplierData.byOrganization || []).map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                              ))}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Top Suppliers Across Organizations */}
                  <Grid item xs={12}>
                    <Card sx={cardStyle}>
                      <Box sx={cardHeaderStyle}>
                        <Typography variant="h6">Top Suppliers Across Organizations</Typography>
                      </Box>
                      <CardContent>
                        <TanStackTable
                          data={supplierData.suppliers || []}
                          columns={[
                            { header: 'Supplier', accessorKey: 'name' },
                            { header: 'Organization', accessorKey: 'orgName' },
                            { header: 'Orders', accessorKey: 'orderCount' },
                            { header: 'Total Purchases', accessorKey: 'totalPurchased', cell: ({ getValue }) => <CurrencyFormatter value={getValue()} /> },
                            { header: 'Avg. Order', accessorKey: 'averageOrderValue', cell: ({ getValue }) => <CurrencyFormatter value={getValue()} /> },
                            { header: 'Last Order', accessorKey: 'lastOrderDate', cell: ({ getValue }) => getValue ? formatDateToStandard(getValue()) : 'N/A' },
                          ]}
                        />
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12}>
                    <Card sx={cardStyle}>
                      <Box sx={cardHeaderStyle}>
                        <Typography variant="h6">Supplier Details</Typography>
                      </Box>
                      <CardContent>
                        <TanStackTable
                          data={supplierData.suppliers || []}
                          columns={supplierDetailsColumns}
                        />
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              )}
            </>
          )}
        </Box>
      )}
    </Paper>
  );
};

export default UniversalReportsPage;
